type AnyListener = (...args: never[]) => unknown;

type EventNotifierListener<TListener extends AnyListener> = (
  ...args: [...Parameters<TListener>, ReturnType<TListener> | undefined]
) => ReturnType<TListener>;

export function createEventNotifier<TListener extends AnyListener>() {
  let isSleeping = false;

  const listeners: EventNotifierListener<TListener>[] = [];

  return {
    addListener(callback: EventNotifierListener<TListener>) {
      listeners.push(callback);
    },

    sleep() {
      isSleeping = true;
    },

    wake() {
      isSleeping = false;
    },

    notifyListeners(...args: Parameters<TListener>) {
      if (isSleeping) {
        return undefined;
      }

      let previousResult: ReturnType<TListener> | undefined = undefined;

      for (const callback of listeners) {
        try {
          const result = callback(...args, previousResult);

          if (result !== undefined) {
            previousResult = result;
          }
        } catch (error) {
          console.error(error);
        }
      }

      return previousResult;
    },
  };
}

export type EventNotifier<TListener extends AnyListener> = ReturnType<
  typeof createEventNotifier<TListener>
>;

export type EventNotifierRegisterListener<TListener extends AnyListener> =
  EventNotifier<TListener>["addListener"];
